// 1. React and built-in hooks
import React, { Fragment, useContext } from 'react';

// 2. Third-party libraries
import { Dialog, Transition } from '@headlessui/react';
import { useMsal } from '@azure/msal-react';

// 3. Local imports
import { deleteChatHistory, getValidToken } from '../utils';
import { APIContext } from '../contexts/APIContext';
import { SessionContext } from '../contexts/SessionContext';
import { ChatHistoryItem } from '../types';
import { ChatbotContext } from '../contexts/ChatbotContext';

interface DeleteHistoryModalProps {
  showDeleteHistoryModal: boolean;
  setShowDeleteHistoryModal: (showDeleteHistoryModal: boolean) => void;
  selectedChatHistoryItem: ChatHistoryItem;
}

export const DeleteHistoryModal: React.FC<DeleteHistoryModalProps> = ({
  showDeleteHistoryModal,
  setShowDeleteHistoryModal,
  selectedChatHistoryItem: { genArcPromptAnalyticsUID, displayName, promptName },
}) => {
  const { accounts } = useMsal();
  const {
    chatSessionId,
    websocketConnectionRef: { current: websocket },
  } = useContext(APIContext);
  const { setIsUpdatingChatHistory } = useContext(SessionContext);
  const { chatWindowConversations, setChatWindowConversations } = useContext(ChatbotContext);

  const deleteHistory = async () => {
    const token = await getValidToken();
    setIsUpdatingChatHistory({ state: true, action: 'delete' });
    setShowDeleteHistoryModal(false);
    deleteChatHistory(accounts, websocket, chatSessionId, displayName, true, genArcPromptAnalyticsUID, token);
    deletePrompt(genArcPromptAnalyticsUID);
  };

  /*
    Function to filter the conversations which are not of the prompt which is being deleted
    updates the ChatWindowConversations excluding deleted prompt.
  */
  const deletePrompt = (genArcPromptAnalyticsUID: string) => {
    const updatedConversations = chatWindowConversations.filter((conversation: any) => {
      const message = conversation.message;
      return !(typeof message === 'object'
        ? message.GenArcPromptAnalyticsUId === genArcPromptAnalyticsUID
        : message === promptName);
    });
    setChatWindowConversations(updatedConversations);
  };

  return (
    <Transition.Root show={showDeleteHistoryModal} as={Fragment}>
      <Dialog as="div" className="relative z-[101]" onClose={() => setShowDeleteHistoryModal(false)}>
        {/* Opaque overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-70 transition-opacity" />
        </Transition.Child>

        {/* Modal panel */}
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <div className="flex flex-col items-center flex-shrink-0 lg:w-1/2">
                <Dialog.Panel className="relative flex border-t-4 border-t-[#A100FF] w-[882px] h-[340px] transform flex-col bg-gamma-modal-bg font-graphik text-black transition-all">
                  <div className="flex flex-row justify-end pr-[24px] pt-[28px]">
                    <img
                      src="/icons/Close.svg"
                      alt="Send suggested prompt"
                      className="w-[16px] h-[16px] cursor-pointer"
                      onClick={() => setShowDeleteHistoryModal(false)}
                    />
                  </div>
                  <div className="relative flex transform flex-col gap-[24px] bg-[var(--Surface-card-bg, #FFFFFF)] shadow-md pt-[56px] pb-[96px]">
                    <Dialog.Title className="text-black font-graphik text-[24px] font-normal flex justify-center items-center gap-8">
                      <img className="h-14 w-14" src="./icons/icon-caution.svg" alt="Caution Image" />
                      <span>Are you sure you want to delete it permanently ?</span>
                    </Dialog.Title>
                  </div>
                  <div className="flex flex-row flex-nowrap justify-end gap-5 pr-[32px] pt-[24px] text-sm">
                    <button
                      className="rounded-sm border border-[#000] bg-[#FFFFFF] px-8 py-[6px] hover:text-[#460073] hover:border-[#460073]"
                      onClick={() => {
                        setShowDeleteHistoryModal(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="rounded-sm bg-[#000000] px-8 py-[6px] text-white hover:bg-[#460073]"
                      onClick={deleteHistory}
                    >
                      Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
